/**
 * Mock implementation of the subscription service for development and testing.
 *
 * This provides a working implementation that doesn't require Stripe integration,
 * allowing development of premium features before the subscription system is implemented.
 *
 * TODO: Replace with actual Stripe SDK integration when implementing subscriptions.
 */

import type {
  ISubscriptionService,
  UserEntitlements,
  UserSubscription,
  PurchaseResult,
  Entitlement,
  SubscriptionsResponse,
} from './types';

/**
 * Mock subscription service for development.
 *
 * By default, all users have no entitlements (free tier).
 * Can be configured to simulate premium access for testing.
 */
export class MockSubscriptionService implements ISubscriptionService {
  private mockSubscription: UserSubscription | null = null;
  private currentUserId: string | null = null;

  constructor(userId?: string) {
    this.currentUserId = userId ?? null;
    // Mock service initialized - using mock implementation for development
  }

  /**
   * Set the current user ID.
   * In a real implementation, this would come from the auth context.
   */
  setUserId(userId: string): void {
    this.currentUserId = userId;
  }

  /**
   * Check if user has a specific entitlement.
   * In mock mode, this always returns false unless explicitly set.
   */
  async hasEntitlement(entitlementId: string): Promise<boolean> {
    // Mock check for entitlement

    if (!this.mockSubscription || !this.mockSubscription.entitlements) {
      return false;
    }

    const entitlement = this.mockSubscription.entitlements[entitlementId];
    return entitlement?.isActive || false;
  }

  /**
   * Get all active entitlements.
   * In mock mode, returns empty object unless explicitly set.
   */
  async getActiveEntitlements(): Promise<UserEntitlements> {
    // Mock retrieval of active entitlements

    if (!this.mockSubscription || !this.mockSubscription.entitlements) {
      return {};
    }

    // Filter to only active entitlements
    const activeEntitlements: UserEntitlements = {};
    for (const [id, entitlement] of Object.entries(
      this.mockSubscription.entitlements
    )) {
      if (entitlement.isActive) {
        activeEntitlements[id] = entitlement;
      }
    }

    return activeEntitlements;
  }

  /**
   * Get the full subscription data.
   */
  async getSubscription(): Promise<UserSubscription | null> {
    // Mock retrieval of subscription data
    return this.mockSubscription;
  }

  /**
   * Get all subscriptions (plural endpoint mock).
   *
   * Note: Mock service stores UserSubscription with entitlements,
   * but must return SubscriptionsResponse format. This method
   * derives subscription metadata from active entitlements.
   */
  async getSubscriptions(): Promise<SubscriptionsResponse> {
    // No subscription data available
    if (!this.mockSubscription?.entitlements) {
      return { subscriptions: [] };
    }

    // Find active entitlements to derive subscription status
    const activeEntitlements = Object.values(
      this.mockSubscription.entitlements
    ).filter(e => e.isActive);

    // No active entitlements means no active subscription
    if (activeEntitlements.length === 0) {
      return { subscriptions: [] };
    }

    // In single-subscription model, use first active entitlement
    // to build subscription metadata
    const primaryEntitlement = activeEntitlements[0];

    return {
      subscriptions: [
        {
          subscriptionId: this.mockSubscription.uid,
          productId: primaryEntitlement.productIdentifier || 'mock-product-id',
          status: 'active',
          expiresAt: primaryEntitlement.periodEndDate?.toISOString() ?? null,
          willRenew: primaryEntitlement.willRenew,
          sources: ['subscription'] as string[],
        },
      ],
    };
  }

  /**
   * Get entitlement expiration date.
   */
  async getEntitlementExpiration(entitlementId: string): Promise<Date | null> {
    // Mock retrieval of entitlement expiration
    if (!this.mockSubscription || !this.mockSubscription.entitlements) {
      return null;
    }

    const entitlement = this.mockSubscription.entitlements[entitlementId];
    if (!entitlement?.isActive) {
      return null;
    }

    return entitlement.periodEndDate;
  }

  /**
   * Mock purchase implementation.
   * In development, this simulates a successful purchase.
   */
  async purchaseProduct(productId: string): Promise<PurchaseResult> {
    // Mock purchase simulation

    // Simulate a delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    // In mock mode, always succeed and grant premium entitlement
    const periodEndDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000); // 30 days
    const mockEntitlement: Entitlement = {
      id: 'heapsterlive',
      isActive: true,
      willRenew: true,
      periodEndDate,
      stripePeriodEndDate: periodEndDate,
      purchaseDate: new Date(),
      productIdentifier: productId,
    };

    this.mockSubscription = {
      uid: this.currentUserId ?? 'mock-user',
      entitlements: {
        heapsterlive: mockEntitlement,
      },
      updatedAt: new Date(),
      createdAt: new Date(),
    };

    return {
      success: true,
      entitlements: ['heapsterlive'],
    };
  }

  /**
   * Mock restore purchases.
   * In development, this does nothing.
   */
  async restorePurchases(): Promise<void> {
    // Mock restore purchases simulation

    // Simulate a delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    // In mock mode, we could restore some test entitlements
    // For now, this is a no-op
  }

  /**
   * Get available products and pricing information.
   * Returns mock product data for testing.
   */
  async getProducts(): Promise<{
    products: Array<{
      productId: string;
      name: string;
      description: string;
      entitlement: string;
      prices: Array<{
        currency: string;
        amount: number;
        priceId: string;
        interval: string;
      }>;
    }>;
  }> {
    // Return mock HeapsterLive product
    return {
      products: [
        {
          productId: 'prod_mock_heapsterlive',
          name: 'HeapsterLive',
          description: 'Access to HeapsterLive',
          entitlement: 'heapsterlive',
          prices: [
            {
              currency: 'usd',
              amount: 299, // $2.99
              priceId: 'price_mock_usd_monthly',
              interval: 'month',
            },
            {
              currency: 'gbp',
              amount: 200, // £2.00
              priceId: 'price_mock_gbp_monthly',
              interval: 'month',
            },
          ],
        },
      ],
    };
  }

  /**
   * Set mock subscription data for testing.
   * This is only available in the mock implementation.
   */
  setMockSubscription(subscription: UserSubscription | null): void {
    // Set mock subscription data for testing
    this.mockSubscription = subscription;
  }

  /**
   * Simulate premium access for testing.
   * This is only available in the mock implementation.
   */
  simulatePremiumAccess(daysRemaining = 30): void {
    // Simulate premium access for testing
    const periodEndDate = new Date(
      Date.now() + daysRemaining * 24 * 60 * 60 * 1000
    );

    this.mockSubscription = {
      uid: this.currentUserId ?? 'mock-user',
      entitlements: {
        heapsterlive: {
          id: 'heapsterlive',
          isActive: true,
          willRenew: true,
          periodEndDate,
          stripePeriodEndDate: periodEndDate,
          purchaseDate: new Date(),
          productIdentifier: 'com.heapster.premium.monthly',
        },
      },
      updatedAt: new Date(),
      createdAt: new Date(),
    };
  }

  /**
   * Clear mock subscription (simulate free tier).
   * This is only available in the mock implementation.
   */
  clearMockSubscription(): void {
    // Clear mock subscription (simulate free tier)
    this.mockSubscription = null;
  }
}

/**
 * Create a mock subscription service instance.
 * This will be replaced with real service creation when Stripe is integrated.
 */
export function createMockSubscriptionService(
  userId?: string
): ISubscriptionService {
  return new MockSubscriptionService(userId);
}

// TODO: When implementing Stripe integration:
// 1. Install Stripe React Native/Web SDK
// 2. Create real implementation of ISubscriptionService using Stripe SDK
// 3. Update the factory function to return real service in production
// 4. Keep mock service for local development and testing
// 5. Add proper error handling and retry logic
// 6. Implement proper caching of subscription status
// 7. Add analytics tracking for purchase events
